import { useState } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import {
  DailyScheduleEmployeeDTO,
  DailyScheduleFloaterBlockDTO,
  DailyScheduleFloaterDTO,
} from '../../../../interfaces/DailyScheduleInterface';
import Time12Input from './Time12Input';

interface Props {
  floaters: DailyScheduleFloaterDTO[];
  employees: DailyScheduleEmployeeDTO[];
  scheduleId: string;
  onAddFloater: (employeeId: string | null) => Promise<void>;
  onUpdateFloater: (floaterId: string, employeeId: string | null) => Promise<void>;
  onRemoveFloater: (floaterId: string) => Promise<void>;
  onAddBlock: (floaterId: string, data: Partial<DailyScheduleFloaterBlockDTO>) => Promise<void>;
  onUpdateBlock: (floaterId: string, blockId: string, data: Partial<DailyScheduleFloaterBlockDTO>) => Promise<void>;
  onRemoveBlock: (floaterId: string, blockId: string) => Promise<void>;
}

function FloaterBlockRow({
  block,
  floaterId,
  onUpdate,
  onRemove,
}: {
  block: DailyScheduleFloaterBlockDTO;
  floaterId: string;
  onUpdate: (floaterId: string, blockId: string, data: Partial<DailyScheduleFloaterBlockDTO>) => Promise<void>;
  onRemove: (floaterId: string, blockId: string) => Promise<void>;
}) {
  const [startTime, setStartTime] = useState(block.startTime ?? '');
  const [endTime, setEndTime] = useState(block.endTime ?? '');
  const [locationName, setLocationName] = useState(block.locationName ?? '');
  const [notes, setNotes] = useState(block.notes ?? '');
  const [saving, setSaving] = useState(false);

  const save = async (field: string, value: string | null) => {
    setSaving(true);
    try {
      await onUpdate(floaterId, block.id, { [field]: value || null });
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td style={{ width: 90 }}>
        <Time12Input
          value={startTime}
          onChange={setStartTime}
          onBlur={() => save('startTime', startTime)}
          style={{ width: 80, fontSize: '0.82rem' }}
        />
      </td>
      <td style={{ width: 90 }}>
        <Time12Input
          value={endTime}
          onChange={setEndTime}
          onBlur={() => save('endTime', endTime)}
          style={{ width: 80, fontSize: '0.82rem' }}
        />
      </td>
      <td style={{ minWidth: 120 }}>
        <Input
          type="text"
          bsSize="sm"
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          onBlur={() => save('locationName', locationName)}
          placeholder="Room / Location..."
        />
      </td>
      <td style={{ minWidth: 140 }}>
        <Input
          type="textarea"
          bsSize="sm"
          rows={1}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => save('notes', notes)}
          placeholder="Notes..."
        />
      </td>
      <td style={{ width: 40, textAlign: 'center' }}>
        {saving ? (
          <Spinner size="sm" style={{ width: 14, height: 14 }} />
        ) : (
          <Button
            color="link"
            size="sm"
            className="text-danger p-0"
            onClick={() => onRemove(floaterId, block.id)}
            style={{ fontSize: '1rem', lineHeight: 1 }}
          >
            ×
          </Button>
        )}
      </td>
    </tr>
  );
}

const FloaterSection = ({
  floaters,
  employees,
  onAddFloater,
  onUpdateFloater,
  onRemoveFloater,
  onAddBlock,
  onUpdateBlock,
  onRemoveBlock,
}: Props) => {
  const [addingFloater, setAddingFloater] = useState(false);
  const [newFloaterEmpId, setNewFloaterEmpId] = useState('');
  const [addingBlockForId, setAddingBlockForId] = useState<string | null>(null);

  const handleAddFloater = async () => {
    setAddingFloater(true);
    try {
      await onAddFloater(newFloaterEmpId || null);
      setNewFloaterEmpId('');
    } finally {
      setAddingFloater(false);
    }
  };

  const handleAddBlock = async (floaterId: string) => {
    setAddingBlockForId(floaterId);
    try {
      await onAddBlock(floaterId, { sortOrder: 0 });
    } finally {
      setAddingBlockForId(null);
    }
  };

  return (
    <div className="section-card mb-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <span className="section-title mb-0" style={{ display: 'inline-block' }}>
          Floaters
        </span>
      </div>

      {floaters.length === 0 && (
        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
          No floaters added yet.
        </p>
      )}

      {floaters.map((floater) => {
        const empName = floater.employee
          ? `${floater.employee.user.firstName} ${floater.employee.user.lastName}`.trim()
          : 'Unassigned Floater';

        return (
          <div key={floater.id} className="floater-card mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#fb6340',
                    color: '#fff',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {empName.charAt(0).toUpperCase()}
                </div>
                <Input
                  type="select"
                  bsSize="sm"
                  value={floater.employeeId ?? ''}
                  style={{ maxWidth: 200 }}
                  onChange={(e) => onUpdateFloater(floater.id, e.target.value || null)}
                >
                  <option value="">-- Unassigned --</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.user.firstName} {emp.user.lastName}
                    </option>
                  ))}
                </Input>
                <span
                  style={{
                    fontSize: '0.68rem',
                    background: '#ffe0b2',
                    color: '#e65100',
                    borderRadius: 2,
                    padding: '0.15rem 0.4rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  FLOATER
                </span>
              </div>
              <Button
                color="danger"
                outline
                size="sm"
                onClick={() => onRemoveFloater(floater.id)}
                style={{ fontSize: '0.75rem' }}
              >
                Remove
              </Button>
            </div>

            <div className="floater-blocks-wrapper">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle }}>Start</th>
                    <th style={{ ...thStyle }}>End</th>
                    <th style={{ ...thStyle }}>Location</th>
                    <th style={{ ...thStyle }}>Notes</th>
                    <th style={{ ...thStyle, width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {floater.blocks.map((block) => (
                    <FloaterBlockRow
                      key={block.id}
                      block={block}
                      floaterId={floater.id}
                      onUpdate={onUpdateBlock}
                      onRemove={onRemoveBlock}
                    />
                  ))}
                  {floater.blocks.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-muted text-center"
                        style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                      >
                        No blocks yet. Click &quot;+ Add Block&quot; to add time slots.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="mt-2">
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  disabled={addingBlockForId === floater.id}
                  onClick={() => handleAddBlock(floater.id)}
                  style={{ fontSize: '0.75rem' }}
                >
                  {addingBlockForId === floater.id ? <Spinner size="sm" /> : '+ Add Block'}
                </Button>
              </div>
            </div>
          </div>
        );
      })}

      <div className="d-flex align-items-center mt-3" style={{ gap: '0.5rem' }}>
        <Input
          type="select"
          bsSize="sm"
          value={newFloaterEmpId}
          onChange={(e) => setNewFloaterEmpId(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          <option value="">-- Select Floater Employee --</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.user.firstName} {emp.user.lastName}
            </option>
          ))}
        </Input>
        <Button color="primary" outline size="sm" disabled={addingFloater} onClick={handleAddFloater}>
          {addingFloater ? <Spinner size="sm" /> : '+ Add Floater'}
        </Button>
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  background: '#f8f9fe',
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#8898aa',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #e9ecef',
};

export default FloaterSection;
